'use client'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldError, FieldGroup } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { useChangePassword } from '@/hooks/useApi'
import { endSession } from '@/lib/auth-session'
import { getErrorMessage } from '@/lib/form'
import { useForm } from '@tanstack/react-form'
import { InfoIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

type ChangePasswordDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  daysUntilExpiration?: number
}

export default function ChangePasswordDialog({ open, onOpenChange, daysUntilExpiration }: ChangePasswordDialogProps) {
  const t = useTranslations('auth.changePassword')
  const tCommon = useTranslations('common')
  const changePassword = useChangePassword()
  const mustChangePassword = daysUntilExpiration !== undefined && daysUntilExpiration <= 0

  const changePasswordSchema = z
    .object({
      currentPassword: z.string().min(1, t('validation.currentRequired')),
      newPassword: z
        .string()
        .min(1, t('validation.newRequired'))
        .min(8, t('validation.lengthMessage'))
        .max(20, t('validation.lengthMessage')),
      confirmNewPassword: z.string().min(1, t('validation.confirmRequired')),
    })
    .refine(({ newPassword, confirmNewPassword }) => !confirmNewPassword || newPassword === confirmNewPassword, {
      path: ['confirmNewPassword'],
      message: t('validation.noMatch'),
    })

  const form = useForm({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
    validators: {
      onChange: changePasswordSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        await changePassword.mutateAsync({
          old_password: value.currentPassword,
          new_password: value.newPassword,
        })
        toast.success(t('success'), { description: t('successDescription') })
        onOpenChange(false)
      } catch {
        toast.error(t('error'), { description: t('errorDescription') })
      }
    },
  })

  useEffect(() => {
    if (!open) return

    form.reset()
    changePassword.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const renderPasswordField = (name: 'currentPassword' | 'newPassword' | 'confirmNewPassword', placeholder: string) => (
    <form.Field name={name}>
      {(field) => {
        const errorMessage = field.state.meta.errors.map(getErrorMessage).filter(Boolean).join(', ')
        const isInvalid = field.state.meta.isTouched && !!errorMessage

        return (
          <Field data-invalid={isInvalid} className="gap-1">
            <Input
              type="password"
              placeholder={placeholder}
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(event) => field.handleChange(event.target.value)}
              required
              maxLength={128}
              className="h-12 rounded-2xl"
              aria-invalid={isInvalid}
            />
            {errorMessage && (
              <FieldError className="flex items-center gap-1 text-[12px] font-semibold text-[#F49090]">
                <InfoIcon className="size-3" />
                {errorMessage}
              </FieldError>
            )}
          </Field>
        )
      }}
    </form.Field>
  )

  return (
    <Dialog
      open={open}
      disablePointerDismissal={mustChangePassword}
      onOpenChange={(nextOpen, details) => {
        if (!nextOpen && mustChangePassword) {
          details.cancel()
          return
        }
        onOpenChange(nextOpen)
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="flex max-w-full flex-col gap-8 rounded-3xl bg-[#FBFCFD] p-10 shadow-2xl ring-0 sm:max-w-160"
      >
        <form
          onSubmit={(event) => {
            event.preventDefault()
            event.stopPropagation()
            form.handleSubmit()
          }}
          className="flex flex-col gap-8"
        >
          <DialogTitle className="text-2xl leading-9 font-semibold text-[#2D2D2D]">{t('title')}</DialogTitle>

          {daysUntilExpiration !== undefined && (
            <DialogDescription>
              {mustChangePassword ? t('expired') : t('expirationWarning', { days: daysUntilExpiration })}
            </DialogDescription>
          )}

          <FieldGroup className="gap-6">
            {renderPasswordField('currentPassword', t('currentPassword'))}
            {renderPasswordField('newPassword', t('newPassword'))}
            {renderPasswordField('confirmNewPassword', t('confirmNewPassword'))}
          </FieldGroup>

          <DialogFooter className="m-0 flex-row justify-end gap-4 rounded-none border-0 bg-transparent p-0">
            {mustChangePassword ? (
              <Button type="button" variant="secondary" className="h-10 w-34 rounded-full" onClick={() => endSession()}>
                {tCommon('logout')}
              </Button>
            ) : (
              <DialogClose render={<Button type="button" variant="secondary" className="h-10 w-34 rounded-full" />}>
                {tCommon('cancel')}
              </DialogClose>
            )}
            <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
              {([canSubmit, isSubmitting]) => (
                <Button
                  type="submit"
                  disabled={!canSubmit || isSubmitting || changePassword.isPending}
                  className="h-10 w-34 rounded-full"
                >
                  {changePassword.isPending ? t('saving') : t('save')}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
